<!-- SchemaBuilder.vue -->
<template>
  <div class="space-y-6">
    <!-- Header with Add Button -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-medium text-gray-100">{{ title }}</h2>
        <p class="text-sm text-gray-400 mt-1">{{ subtitle }}</p>
      </div>
      <button
        @click="addField"
        class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        Add Field
      </button>
    </div>

    <!-- Schema Fields -->
    <div class="space-y-4">
      <div
        v-for="(field, index) in schema"
        :key="index"
        class="bg-dark-secondary rounded-lg border border-gray-700 hover:border-primary/30 transition-all duration-300"
      >
        <!-- Field Header -->
        <div 
          class="p-4 flex items-center justify-between cursor-pointer select-none"
          @click="toggleField(index)"
        >
          <div class="flex items-center gap-3">
            <div 
              class="transform transition-transform duration-200"
              :class="expandedFields[index] ? 'rotate-90' : ''"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-300">{{ field.displayName || 'New Field' }}</span>
            <span class="text-xs text-gray-500">{{ field.key || 'generated_key' }}</span>
            <span 
              class="px-2 py-0.5 text-xs rounded-full"
              :class="{
                'bg-blue-500/10 text-blue-400': field.type === 'string',
                'bg-green-500/10 text-green-400': field.type === 'number',
                'bg-yellow-500/10 text-yellow-400': field.type === 'boolean',
                'bg-purple-500/10 text-purple-400': field.type === 'array'
              }"
            >
              {{ getTypeLabel(field.type) }}
            </span>
          </div>
          <div class="flex items-center gap-3" @click.stop>
            <button
              @click="removeField(index)"
              class="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-dark"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Field Content -->
        <div 
          v-show="expandedFields[index]"
          class="p-4 space-y-4 border-t border-gray-700"
        >
          <!-- Basic Fields -->
          <div class="grid grid-cols-1 gap-4">
            <!-- Field Name and Key -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-300">
                Field Name & Key
                <span class="text-red-500">*</span>
              </label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <input
                    v-model="field.displayName"
                    type="text"
                    placeholder="e.g., User Name"
                    class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    @input="updateFieldKey(field)"
                  />
                  <p class="mt-1.5 text-xs text-gray-500">
                    Enter a human-readable name
                  </p>
                </div>
                <div>
                  <div class="flex items-center gap-2 px-3 py-2 bg-dark/50 border border-gray-700 rounded-lg text-gray-400 text-sm">
                    <span>{{ field.key || 'generated_key' }}</span>
                    <span v-if="!field.key" class="text-gray-600 italic text-xs">(auto-generated)</span>
                  </div>
                  <p class="mt-1.5 text-xs text-gray-500">
                    Auto-generated snake_case key
                  </p>
                </div>
              </div>
            </div>

            <!-- Field Type -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-300">
                Field Type
                <span class="text-red-500">*</span>
              </label>
              <select
                v-model="field.type"
                class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                @change="handleTypeChange(field)"
              >
                <option v-for="type in supportedTypes" :key="type.value" :value="type.value">
                  {{ type.label }}
                </option>
              </select>
              <p class="text-xs text-gray-500">
                Choose how this field should store its data
              </p>
            </div>

            <!-- Description -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-300">
                Description
                <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="field.description"
                rows="2"
                placeholder="Describe what this field is used for..."
                class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              ></textarea>
            </div>
          </div>

          <!-- Array Settings -->
          <div v-if="field.type === 'array'" class="border-t border-gray-700 pt-4 space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-medium text-gray-300">Array Settings</h4>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-500">Min</span>
                  <input
                    v-model.number="field.minItems"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-20 px-2 py-1 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-500">Max</span>
                  <input
                    v-model.number="field.maxItems"
                    type="number"
                    min="1"
                    placeholder="10"
                    class="w-20 px-2 py-1 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4">
              <!-- Array Item Type -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-300">
                  Item Type
                  <span class="text-red-500">*</span>
                </label>
                <select
                  :value="field.items?.type || 'string'"
                  @input="updateArrayItemType(field, $event)"
                  class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option
                    v-for="type in supportedTypes.filter(t => t.value !== 'array')"
                    :key="type.value"
                    :value="type.value"
                  >
                    {{ type.label }}
                  </option>
                </select>
              </div>

              <!-- Array Item Description -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-300">
                  Item Description
                  <span class="text-red-500">*</span>
                </label>
                <textarea
                  :value="field.items?.description || ''"
                  @input="updateArrayItemDescription(field, $event)"
                  rows="2"
                  placeholder="Describe what each item in the array represents..."
                  class="w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Validation Errors -->
          <div v-if="field.errors?.length" class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p v-for="error in field.errors" :key="error" class="text-sm text-red-400">
              {{ error }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="schema.length === 0"
      class="text-center py-8 bg-dark-secondary rounded-lg border border-gray-700"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      <p class="mt-2 text-gray-400">No fields added yet. Click "Add Field" to start building your schema.</p>
    </div>

    <!-- Save Button -->
    <div class="flex justify-end items-center gap-3">
      <span v-if="lastSaved" class="text-xs text-gray-500">
        Last saved: {{ new Date(lastSaved).toLocaleString() }}
      </span>
      <button
        @click="saveSchema"
        :disabled="!isValid || schema.length === 0"
        class="px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        :class="[
          saveSuccess ? 'bg-green-500 hover:bg-green-500/90 text-white' : 'bg-primary hover:bg-primary/90 text-white'
        ]"
      >
        <span v-if="props.isSaving" class="inline-block animate-spin">
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
        <span v-else-if="saveSuccess" class="inline-block">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span>{{ props.isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Schema' }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

export interface SchemaField {
  displayName: string
  key: string
  type: 'string' | 'number' | 'boolean' | 'array'
  description: string
  minItems?: number
  maxItems?: number
  items?: {
    type: string
    description: string
  }
  errors?: string[]
}

interface TypeOption {
  value: 'string' | 'number' | 'boolean' | 'array'
  label: string
}

const supportedTypes: TypeOption[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'True/False' },
  { value: 'array', label: 'List' }
]

const getTypeLabel = (type: string) => {
  return supportedTypes.find(t => t.value === type)?.label || type
}

const props = defineProps<{
  modelValue: SchemaField[]
  isSaving?: boolean
  title?: string
  subtitle?: string
}>()

const schema = ref<SchemaField[]>([])
const isValid = ref(true)
const expandedFields = ref<boolean[]>([])
const lastSaved = ref<number | null>(null)
const saveSuccess = ref(false)

// Initialize schema from props
watch(() => props.modelValue, (newValue) => {
  schema.value = [...(newValue || [])]
}, { immediate: true })

// Watch for changes in field properties that should trigger validation
watch(() => schema.value.map(field => ({
  key: field.key,
  type: field.type,
  description: field.description,
  items: field.type === 'array' ? {
    type: field.items?.type,
    description: field.items?.description
  } : undefined,
  minItems: field.minItems,
  maxItems: field.maxItems
})), () => {
  validateSchema()
}, { deep: true, immediate: true })

// Watch schema length to keep expandedFields in sync
watch(() => schema.value.length, (newLength: number) => {
  // Ensure expandedFields array matches schema length
  expandedFields.value = expandedFields.value.slice(0, newLength)
  while (expandedFields.value.length < newLength) {
    expandedFields.value.push(false)
  }
}, { immediate: true })

const addField = () => {
  const newField: SchemaField = {
    displayName: '',
    key: '',
    type: 'string',
    description: '',
    errors: []
  }
  
  // Initialize array properties if type is array
  if (newField.type === 'array') {
    newField.items = {
      type: 'string',
      description: ''
    }
    newField.minItems = 0
    newField.maxItems = 10
  }
  
  schema.value.push(newField)
  // New field will be automatically expanded by the watcher
  expandedFields.value.push(true)
  validateSchema()
}

const toggleField = (index: number) => {
  if (index >= 0 && index < expandedFields.value.length) {
    expandedFields.value[index] = !expandedFields.value[index]
  }
}

const updateFieldKey = (field: SchemaField) => {
  // Convert display name to snake_case
  field.key = field.displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
}

const removeField = (index: number) => {
  if (index >= 0 && index < schema.value.length) {
    schema.value.splice(index, 1)
    expandedFields.value.splice(index, 1)
    validateSchema()
  }
}

const handleTypeChange = (field: SchemaField) => {
  // Initialize or remove array-specific properties
  if (field.type === 'array') {
    // Ensure items object is properly initialized
    field.items = {
      type: 'string',
      description: ''
    }
    field.minItems = 0
    field.maxItems = 10
  } else {
    // Clean up array properties if type is changed from array
    field.items = undefined
    field.minItems = undefined
    field.maxItems = undefined
  }

  // Validation will be triggered by the watcher
}

function validateSchema() {
  const newErrors = new Map<number, string[]>()
  
  // Validate each field
  schema.value.forEach((field, index) => {
    const errors: string[] = []
    
    // Validate key
    if (!field.key) {
      errors.push('Field key is required')
    } else if (!/^[a-z][a-z0-9_]*$/.test(field.key)) {
      errors.push('Field key must be in snake_case and start with a letter')
    }

    // Validate description
    if (!field.description?.trim()) {
      errors.push('Description is required')
    }

    // Validate array specific fields
    if (field.type === 'array') {
      if (!field.items?.description?.trim()) {
        errors.push('Array item description is required')
      }
      if (field.minItems !== undefined && field.maxItems !== undefined && field.minItems > field.maxItems) {
        errors.push('Minimum items cannot be greater than maximum items')
      }
    }

    if (errors.length > 0) {
      newErrors.set(index, errors)
    }
  })

  // Check for duplicate keys
  const keys = schema.value.map(field => field.key)
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index)
  
  if (duplicates.length > 0) {
    schema.value.forEach((field, index) => {
      if (duplicates.includes(field.key)) {
        const errors = newErrors.get(index) || []
        errors.push('Duplicate field key')
        newErrors.set(index, errors)
      }
    })
  }

  // Update errors and validity in a single batch
  schema.value.forEach((field, index) => {
    field.errors = newErrors.get(index) || []
  })
  isValid.value = schema.value.length > 0 && newErrors.size === 0
}

const updateArrayItemType = (field: SchemaField, event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  if (!field.items) {
    field.items = {
      type: value,
      description: ''
    }
  } else {
    field.items.type = value
  }
}

const updateArrayItemDescription = (field: SchemaField, event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value
  if (!field.items) {
    field.items = {
      type: 'string',
      description: value
    }
  } else {
    field.items.description = value
  }
}

const saveSchema = () => {
  if (!isValid.value) return
  
  // Emit the schema to parent component
  emit('save', schema.value)
  lastSaved.value = Date.now()
  
  // Show success state temporarily
  saveSuccess.value = true
  setTimeout(() => {
    saveSuccess.value = false
  }, 2000)
}

const emit = defineEmits<{
  (event: 'save', schema: SchemaField[]): void
}>()
</script>
